/**
 * @file billingErrors.ts
 * @brief One map from a billing refusal to the words a user sees and the
 *        Billing screen that fixes it (no dead ends).
 *
 * @details
 * The backend refuses work with a code: `MISSING_PAYMENT_METHOD` (402 on a
 * hold), `UPGRADE_NEEDS_MINUTES` (402 on Notes → full), `BILLING_PENDING`
 * (409 while a meeting's bill is still being settled), `AUTH_REQUIRED` (the
 * bank wants 3D Secure on a hold) and Stripe's card decline codes. Before this
 * file each caller wrote its own toast, and a personal user was told to "add a
 * payment method in Settings", a screen that does not exist on the web.
 *
 * Who can fix it decides the button:
 * - a personal user → their own Billing screen (`/app/billing/...`);
 * - a company admin → the company Billing page;
 * - a company member → nobody on this screen; the message says to ask the
 *   admin, and there is no button (a button to a page they cannot open would
 *   be the dead end this file exists to remove).
 *
 * Blocked countries (B1, 22-10-2026): `COUNTRY_NOT_AVAILABLE` (403 on a plan
 * purchase, a hold or an interpretation start) and `SUBSCRIPTIONS_PAUSED`
 * (403 on a plan purchase) show the server's sentence and no button: a card
 * won't help, so never "Add a card" or "Try again".
 *
 * Free plan (B4, 22-10-2026): `FREE_LIMIT_REACHED` (402) says the free
 * minutes are used and offers Pro, never "add a card" (Free has no overage);
 * `EMAIL_NOT_VERIFIED` (403) shows the server's sentence (it just emailed the
 * link) and no button.
 *
 * Fair use (B6, 22-10-2026): `FAIR_USE_REACHED` (402) on a Pro / Business
 * Meeting Notes start shows the server's sentence ("You've used this month's
 * 60 hours of Meeting Notes. They reset on 1 November.") and no button:
 * never "add a card", nothing to buy.
 *
 * One-time Free (B20 / W17, 24-10-2026): `FREE_LIMIT_REACHED` with
 * `resetMode: "one_time"` says the free minutes are used up (or ended) in
 * the server's words, never "next month"; with `payAsYouGo` (no card yet)
 * the button adds a card, since the next recording can be paid as you go.
 * `HISTORY_LOCKED` (403, now also on Pro / Business when a history window is
 * set) and the Business seat refusals show the server's sentence and no
 * "pick a plan".
 *
 * B43 (31-10-2026, ADR-061): Free is never charged. `FREE_MINUTES_USED` (the
 * new name of `FREE_LIMIT_REACHED`, both read) = the server's sentence +
 * "Pick a plan", never "Add a card"; `reason` device_used / email_not_allowed
 * / import_too_long say why. `FREE_INTEL_LIMIT` (Super Admin capped Full
 * Intelligence): "Full Intelligence is on Pro. Pick a plan." `PLAN_MINUTES_USED`
 * (Pro / Business with plan overage off): the server's sentence, no button.
 *
 * Pure (relative imports only) so node:test can load it.
 *
 * @author DES
 * @date 17-10-2026
 */

import { billingPath, type BillingSection } from "./billingPaths.ts";
import {
  FREE_ENDED_MESSAGE,
  FREE_INTEL_ON_PRO_MESSAGE,
  FREE_USED_UP_MESSAGE,
  FREE_USED_UP_ONCE_MESSAGE,
} from "./freeMinutes.ts";

/** Extra fields some refusals carry (B20 FREE_LIMIT_REACHED). */
export type BillingErrorDetails = {
  resetMode?: string | null;
  payAsYouGo?: boolean | null;
  expired?: boolean | null;
  /** B43: used | expired | device_used | email_not_allowed | import_too_long. */
  reason?: string | null;
};

/** The B20 fields of a refusal body, from an axios error's response data. */
export function billingErrorDetails(body: unknown): BillingErrorDetails | undefined {
  if (!body || typeof body !== "object") return undefined;
  const b = body as Record<string, unknown>;
  const out: BillingErrorDetails = {};
  if (typeof b.resetMode === "string") out.resetMode = b.resetMode;
  if (typeof b.payAsYouGo === "boolean") out.payAsYouGo = b.payAsYouGo;
  if (typeof b.expired === "boolean") out.expired = b.expired;
  if (typeof b.reason === "string") out.reason = b.reason;
  return Object.keys(out).length ? out : undefined;
}

/** Whose billing the signed-in user can change. */
export type BillingAudience = "personal" | "company_admin" | "company_member";

export type BillingErrorView = {
  title: string;
  message: string;
  /** Button to the screen that fixes it; absent when the user cannot fix it. */
  action?: { label: string; to: string };
};

/** The server's words for a blocked country (BACKEND countryAccess.mjs); used only when it sends none. */
export const COUNTRY_NOT_AVAILABLE_FALLBACK =
  "VERBLIZR plans can't be bought from your country. Contact support if you think this is wrong.";
const PAUSED_FALLBACK = "Plans can't be bought right now. Please try again later.";

export type NotAvailable = { code: string; message: string };

/**
 * Why this account can't buy (catalog/status `notAvailable`, B1), or null.
 * A status with `eligible: false` and no `notAvailable` (older backend) reads
 * as a blocked country.
 */
export function notAvailableOf(
  data: { eligible?: boolean | null; notAvailable?: { code?: string | null; message?: string | null } | null } | null | undefined,
): NotAvailable | null {
  const n = data?.notAvailable;
  if (n?.code) return { code: n.code, message: n.message || (n.code === "SUBSCRIPTIONS_PAUSED" ? PAUSED_FALLBACK : COUNTRY_NOT_AVAILABLE_FALLBACK) };
  if (data?.eligible === false) return { code: "COUNTRY_NOT_AVAILABLE", message: COUNTRY_NOT_AVAILABLE_FALLBACK };
  return null;
}

/**
 * The blocked-country sentence when nothing can be paid from here (plans and
 * pay as you go), from `freePlan.notAvailable` or the status `notAvailable`.
 * Null for `SUBSCRIPTIONS_PAUSED`, which stops plans only.
 */
export function countryBlockedMessage(
  status:
    | {
        eligible?: boolean | null;
        notAvailable?: { code?: string | null; message?: string | null } | null;
        freePlan?: { notAvailable?: { code?: string | null; message?: string | null } | null } | null;
      }
    | null
    | undefined,
): string | null {
  const free = status?.freePlan?.notAvailable;
  if (free?.code === "COUNTRY_NOT_AVAILABLE") return free.message || COUNTRY_NOT_AVAILABLE_FALLBACK;
  const n = notAvailableOf(status);
  return n?.code === "COUNTRY_NOT_AVAILABLE" ? n.message : null;
}

/** A refusal no retry or card can fix (B1): the sheet offers Close, not "Try again". */
export function isFinalRefusal(code: string | null | undefined): boolean {
  return code === "COUNTRY_NOT_AVAILABLE" || code === "SUBSCRIPTIONS_PAUSED";
}

/** Stripe decline codes (`error.decline_code` / `error.code`) we name plainly. */
const DECLINE_CODES = new Set([
  "card_declined",
  "generic_decline",
  "insufficient_funds",
  "lost_card",
  "stolen_card",
  "do_not_honor",
  "transaction_not_allowed",
  "fraudulent",
  "PAYMENT_DECLINED",
  "CARD_DECLINED",
]);

const EXPIRED_CODES = new Set(["expired_card", "EXPIRED_CARD"]);
const CVC_CODES = new Set(["incorrect_cvc", "invalid_cvc"]);
const AUTH_CODES = new Set([
  "AUTH_REQUIRED",
  "authentication_required",
  "payment_intent_authentication_failure",
  "setup_intent_authentication_failure",
  "card_not_authenticated",
]);

/** Billing screen for this audience, or null when they cannot open one. */
function fixPath(audience: BillingAudience, section: BillingSection): string | null {
  if (audience === "company_admin") return "/company/billing";
  if (audience === "company_member") return null;
  return billingPath(section);
}

function withAction(
  audience: BillingAudience,
  section: BillingSection,
  label: string,
  view: Omit<BillingErrorView, "action">,
): BillingErrorView {
  const to = fixPath(audience, section);
  return to ? { ...view, action: { label, to } } : view;
}

/**
 * The view for a billing code, or null when the code is not a billing one
 * (the caller then shows its own message).
 */
export function billingErrorView(
  code: string | null | undefined,
  audience: BillingAudience,
  serverMessage?: string | null,
  details?: BillingErrorDetails,
): BillingErrorView | null {
  if (!code) return null;

  // Blocked country (B1): nothing to fix in Billing, so no button.
  if (code === "COUNTRY_NOT_AVAILABLE") {
    return { title: "Not available in your country", message: serverMessage || COUNTRY_NOT_AVAILABLE_FALLBACK };
  }
  if (code === "SUBSCRIPTIONS_PAUSED") {
    return { title: "Plans are paused", message: serverMessage || PAUSED_FALLBACK };
  }

  // Free plan (B4): used up. A card won't help (no overage); Pro will.
  // `FREE_NOTES_LIMIT_REACHED` is the pre-B4 name of the same refusal.
  // B43: FREE_MINUTES_USED is the same refusal; never "Add a card".
  if (code === "FREE_MINUTES_USED" || code === "FREE_LIMIT_REACHED" || code === "FREE_NOTES_LIMIT_REACHED") {
    if (details?.reason === "device_used" || details?.reason === "email_not_allowed") {
      return withAction(audience, "plan", "Pick a plan", {
        title: "No free minutes",
        message: serverMessage || FREE_USED_UP_ONCE_MESSAGE,
      });
    }
    if (details?.reason === "import_too_long") {
      return withAction(audience, "plan", "Pick a plan", {
        title: "Not enough free minutes",
        message: serverMessage || FREE_USED_UP_ONCE_MESSAGE,
      });
    }
    // B20 one-time: used up for good (or ended). B43: no pay as you go.
    if (details?.resetMode === "one_time") {
      const fallback = details.expired ? FREE_ENDED_MESSAGE : FREE_USED_UP_ONCE_MESSAGE;
      return withAction(audience, "plan", "Pick a plan", {
        title: details.expired ? "Free minutes ended" : "Free minutes used",
        message: serverMessage || fallback,
      });
    }
    return withAction(audience, "plan", "See Pro", {
      title: "Free minutes used",
      message: FREE_USED_UP_MESSAGE,
    });
  }
  if (code === "FREE_INTEL_LIMIT") {
    return withAction(audience, "plan", "Pick a plan", {
      title: "Full Intelligence is on Pro",
      message: serverMessage || FREE_INTEL_ON_PRO_MESSAGE,
    });
  }
  if (code === "PLAN_MINUTES_USED") {
    return {
      title: "Meeting Intelligence minutes used",
      message: serverMessage || "You've used this month's Meeting Intelligence minutes. They come back with your plan's next month.",
    };
  }
  if (code === "FAIR_USE_REACHED") {
    return {
      title: "Meeting Notes hours used",
      message: serverMessage || "You've used this month's Meeting Notes hours. They reset with your plan.",
    };
  }
  // B20: a history window on any plan, and the Business seat limits. The
  // server names the real numbers; nothing to buy from here.
  // B39 (28-10-2026): the Free wording (or no message) gets "Pick a plan";
  // a paid plan's window stays a sentence only.
  if (code === "HISTORY_LOCKED") {
    const view = { title: "Older than your plan keeps", message: serverMessage || "This item is older than your plan keeps." };
    return !serverMessage || /free plan|pick a plan/i.test(serverMessage)
      ? withAction(audience, "plan", "Pick a plan", view)
      : view;
  }
  if (code === "TEAM_SEATS_FULL" || code === "TEAM_SEATS_ABOVE_MAXIMUM") {
    return { title: "Business seat limit", message: serverMessage || "Business has reached its maximum seats." };
  }
  if (code === "EMAIL_NOT_VERIFIED") {
    return {
      title: "Confirm your email",
      message: serverMessage || "Please confirm your email first. We've sent you a link.",
    };
  }

  // One account = one person (pricing, DES 18-10-2026): a second live
  // recording or interpretation on the same account. Nothing to buy or fix
  // in Billing, so no button.
  if (code === "ACCOUNT_BUSY") {
    return {
      title: "Already recording",
      message: "This account is already recording on another device.",
    };
  }

  if (code === "MISSING_PAYMENT_METHOD") {
    if (audience === "company_member") {
      return {
        title: "Your company needs a card",
        message: "Your company has no card on file. Ask your company admin to add one, then try again.",
      };
    }
    return withAction(audience, "cards", "Add a card", {
      title: "Add a card to continue",
      message:
        audience === "company_admin"
          ? "Add a company card in Billing, then try again."
          : "You need a card on file before this can start. Add one in Billing, then try again.",
    });
  }

  if (code === "UPGRADE_NEEDS_MINUTES") {
    if (audience === "company_member") {
      return {
        title: "Not enough Intelligence minutes",
        message: serverMessage || "There aren't enough Intelligence minutes left. Ask your company admin.",
      };
    }
    return withAction(audience, "plan", "See plans", {
      title: "Not enough Intelligence minutes",
      message: serverMessage || "There aren't enough Intelligence minutes left in this period for the full run.",
    });
  }

  if (code === "BILLING_PENDING") {
    return withAction(audience, "invoices", "Open invoices", {
      title: "Billing still in progress",
      message: "This meeting's bill is still being settled. Try again in a minute.",
    });
  }

  if (AUTH_CODES.has(code)) {
    return withAction(audience, "cards", "Check cards", {
      title: "Your bank needs to confirm",
      message:
        "Your bank asked you to confirm this payment and it wasn't completed. Try again, or use another card.",
    });
  }

  if (EXPIRED_CODES.has(code)) {
    return withAction(audience, "cards", "Update card", {
      title: "Your card has expired",
      message: "Add a card that's in date, then try again.",
    });
  }

  if (CVC_CODES.has(code)) {
    return {
      title: "Security code is wrong",
      message: "Check the 3-digit code on the back of the card and try again.",
    };
  }

  // A declined plan purchase (night E2E F8, 21-10-2026): 402 with Stripe's
  // own words, which say why. Shown once; the user picks another card.
  if (code === "BILLING_DECLINED") {
    return withAction(audience, "cards", "Use another card", {
      title: "Your card was declined",
      message: serverMessage || "Your bank declined the payment. Try another card, or contact your bank.",
    });
  }

  if (DECLINE_CODES.has(code)) {
    return withAction(audience, "cards", "Use another card", {
      title: "Your card was declined",
      message:
        code === "insufficient_funds"
          ? "Your bank declined it for insufficient funds. Try another card."
          : "Your bank declined the payment. Try another card, or contact your bank.",
    });
  }

  return null;
}

/** The audience for a signed-in user (role + company link). */
export function billingAudienceFor(user: {
  role?: string | null;
  company_id?: unknown;
  companyId?: unknown;
} | null | undefined): BillingAudience {
  const role = user?.role ?? "";
  if (role === "company_admin" || role === "admin") return "company_admin";
  const companyId = user?.company_id ?? user?.companyId;
  return companyId ? "company_member" : "personal";
}
